import axios from '@/axios-instance'

export const ModuleDiscussions = {
  namespaced: true,

  state: {
    threads: [],
    threadsNextPage: null,
    threadsLoading: false,
    threadsError: false,

    currentThread: null,
    posts: [],
    postsNextPage: null,
    postsLoading: false,
    postsError: false,

    postSubmitting: false,
    postSubmitError: null,

    pageThreads: [],
    pageThreadsLoading: false,
    pageThreadsError: false,
  },

  mutations: {
    setThreads(state, data) {
      state.threads = data
    },
    appendThreads(state, data) {
      state.threads = [...state.threads, ...data]
    },
    setThreadsNextPage(state, url) {
      state.threadsNextPage = url
    },
    setThreadsLoading(state, value) {
      state.threadsLoading = value
    },
    setThreadsError(state, value) {
      state.threadsError = value
    },

    setCurrentThread(state, data) {
      state.currentThread = data
    },
    setPosts(state, data) {
      state.posts = data
    },
    appendPosts(state, data) {
      state.posts = [...state.posts, ...data]
    },
    setPostsNextPage(state, url) {
      state.postsNextPage = url
    },
    setPostsLoading(state, value) {
      state.postsLoading = value
    },
    setPostsError(state, value) {
      state.postsError = value
    },

    setPostSubmitting(state, value) {
      state.postSubmitting = value
    },
    setPostSubmitError(state, value) {
      state.postSubmitError = value
    },

    appendPost(state, post) {
      state.posts = [...state.posts, post]
    },
    updatePost(state, updated) {
      state.posts = state.posts.map(p => p.id === updated.id ? updated : p)
    },
    prependThread(state, thread) {
      state.threads = [thread, ...state.threads]
    },
    setPageThreads(state, data) {
      state.pageThreads = data
    },
    prependPageThread(state, thread) {
      state.pageThreads = [thread, ...state.pageThreads]
    },
    setPageThreadsLoading(state, value) {
      state.pageThreadsLoading = value
    },
    setPageThreadsError(state, value) {
      state.pageThreadsError = value
    },
  },

  actions: {
    fetchThreads(context, { organizationId } = {}) {
      context.commit('setThreadsLoading', true)
      context.commit('setThreadsError', false)
      const params = organizationId ? { organization: organizationId } : {}
      return axios.get('/api/v1/discussions/threads/', { params })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : res.data.results
          context.commit('setThreads', data)
          context.commit('setThreadsNextPage', res.data.next ?? null)
        })
        .catch((err) => {
          console.error(err)
          context.commit('setThreadsError', true)
        })
        .finally(() => {
          context.commit('setThreadsLoading', false)
        })
    },

    fetchMoreThreads(context) {
      if (!context.state.threadsNextPage) return
      context.commit('setThreadsLoading', true)
      return axios.get(context.state.threadsNextPage)
        .then((res) => {
          context.commit('appendThreads', res.data.results)
          context.commit('setThreadsNextPage', res.data.next)
        })
        .catch((err) => {
          console.error(err)
          context.commit('setThreadsError', true)
        })
        .finally(() => {
          context.commit('setThreadsLoading', false)
        })
    },

    fetchThread(context, threadId) {
      return axios.get(`/api/v1/discussions/threads/${threadId}/`)
        .then((res) => {
          context.commit('setCurrentThread', res.data)
        })
        .catch((err) => {
          console.error(err)
        })
    },

    fetchPosts(context, threadId) {
      context.commit('setPostsLoading', true)
      context.commit('setPostsError', false)
      return axios.get(`/api/v1/discussions/threads/${threadId}/posts/`)
        .then((res) => {
          context.commit('setPosts', res.data.results)
          context.commit('setPostsNextPage', res.data.next)
        })
        .catch((err) => {
          console.error(err)
          context.commit('setPostsError', true)
        })
        .finally(() => {
          context.commit('setPostsLoading', false)
        })
    },

    fetchMorePosts(context, threadId) {
      if (!context.state.postsNextPage) return
      context.commit('setPostsLoading', true)
      return axios.get(context.state.postsNextPage)
        .then((res) => {
          context.commit('appendPosts', res.data.results)
          context.commit('setPostsNextPage', res.data.next)
        })
        .catch((err) => {
          console.error(err)
          context.commit('setPostsError', true)
        })
        .finally(() => {
          context.commit('setPostsLoading', false)
        })
    },

    createPost(context, { threadId, body }) {
      context.commit('setPostSubmitting', true)
      context.commit('setPostSubmitError', null)
      // Optimistic placeholder
      const u = context.rootState.user?.user
      const optimistic = {
        id: `optimistic-${Date.now()}`,
        body,
        author: u ? {
          id: u.id,
          full_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
          avatar_url: null,
        } : null,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        is_deleted: false,
        _optimistic: true,
      }
      context.commit('appendPost', optimistic)
      return axios.post(`/api/v1/discussions/threads/${threadId}/posts/`, { body })
        .then((res) => {
          // Replace optimistic post with real one
          context.commit('updatePost', { ...optimistic, ...res.data, _optimistic: false })
          return res.data
        })
        .catch((err) => {
          console.error(err)
          // Remove the optimistic post on error
          context.commit('updatePost', { ...optimistic, _failed: true })
          const msg = err.response?.data?.detail || 'Failed to post reply'
          context.commit('setPostSubmitError', msg)
          throw err
        })
        .finally(() => {
          context.commit('setPostSubmitting', false)
        })
    },

    editPost(context, { threadId, postId, body }) {
      context.commit('setPostSubmitting', true)
      context.commit('setPostSubmitError', null)
      return axios.patch(`/api/v1/discussions/threads/${threadId}/posts/${postId}/`, { body })
        .then((res) => {
          context.commit('updatePost', res.data)
          return res.data
        })
        .catch((err) => {
          console.error(err)
          const msg = err.response?.data?.detail || 'Failed to edit post'
          context.commit('setPostSubmitError', msg)
          throw err
        })
        .finally(() => {
          context.commit('setPostSubmitting', false)
        })
    },

    softDeletePost(context, { threadId, postId }) {
      return axios.post(`/api/v1/discussions/threads/${threadId}/posts/${postId}/soft-delete/`)
        .then((res) => {
          context.commit('updatePost', res.data)
        })
        .catch((err) => {
          console.error(err)
        })
    },

    createThread(context, { body, organizationId, pageId = null }) {
      const payload = { page: pageId, body }
      if (organizationId != null) payload.organization = organizationId
      return axios.post('/api/v1/discussions/threads/', payload)
        .then((res) => {
          if (pageId) {
            context.commit('prependPageThread', res.data)
          } else {
            context.commit('prependThread', res.data)
          }
          return res.data
        })
    },

    fetchPageThreads(context, { pageSlug, organizationId } = {}) {
      context.commit('setPageThreadsLoading', true)
      context.commit('setPageThreadsError', false)
      const params = { page_slug: pageSlug }
      if (organizationId) params.organization = organizationId
      return axios.get('/api/v1/discussions/threads/', { params })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : (res.data.results ?? [])
          context.commit('setPageThreads', data)
        })
        .catch((err) => {
          console.error(err)
          context.commit('setPageThreadsError', true)
        })
        .finally(() => {
          context.commit('setPageThreadsLoading', false)
        })
    },

    react(context, { postId, emoji }) {
      return axios.post(`/api/v1/discussions/posts/${postId}/react/`, { emoji })
        .then((res) => res.data)
    },
  },
}
